// TanStack Query hooks for transforms CRUD.
// Atomic saves go through the save_transform RPC (SECURITY DEFINER) per plan §3.1.
// Mirrors useObjectTypes / useAgentFiles pattern.

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { invokeEdgeFunction } from "@/lib/edge-functions";
import type { Transform, TransformTarget, SaveTransformInput } from "@/types/ontology";
import { StaleVersionError } from "@/types/ontology";
import type { RunTransformRequest, RunTransformResponse } from "@/types/edge-functions";

// ── Query keys (plan §1.3) ────────────────────────────────────────────────────

export const TRANSFORMS_KEY = ["transforms"] as const;

// ── Error mapping ─────────────────────────────────────────────────────────────

function mapRpcError(error: { message?: string; code?: string } | null): Error {
  if (!error) return new Error("Unknown error");
  if (error.message?.includes("stale_version") || error.code === "P0001") {
    return new StaleVersionError();
  }
  return new Error(error.message ?? "Unknown error");
}

// ── Queries ───────────────────────────────────────────────────────────────────

/** List all non-deleted transforms. */
export function useTransforms() {
  const query = useQuery({
    queryKey: TRANSFORMS_KEY,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("transforms")
        .select("*")
        .is("deleted_at", null)
        .order("created_at", { ascending: true });

      if (error) throw new Error(error.message);

      return (data as Transform[]) ?? [];
    },
  });

  return { transforms: query.data ?? [], isLoading: query.isLoading, error: query.error };
}

/** List transforms whose source_object_type_id equals the given id. */
export function useTransformsBySource(sourceObjectTypeId: string | null) {
  const query = useQuery({
    queryKey: [...TRANSFORMS_KEY, "source", sourceObjectTypeId],
    enabled: sourceObjectTypeId !== null,
    queryFn: async () => {
      if (!sourceObjectTypeId) return [];
      const { data, error } = await supabase
        .from("transforms")
        .select("*")
        .eq("source_object_type_id", sourceObjectTypeId)
        .is("deleted_at", null)
        .order("created_at", { ascending: true });

      if (error) throw new Error(error.message);
      return (data as Transform[]) ?? [];
    },
  });
  return { transforms: query.data ?? [], isLoading: query.isLoading, error: query.error };
}

/** List transforms whose target_object_type_id equals the given id. */
export function useTransformsByTarget(targetObjectTypeId: string | null) {
  const query = useQuery({
    queryKey: [...TRANSFORMS_KEY, "target", targetObjectTypeId],
    enabled: targetObjectTypeId !== null,
    queryFn: async () => {
      if (!targetObjectTypeId) return [];
      const { data, error } = await supabase
        .from("transforms")
        .select("*")
        .eq("target_object_type_id", targetObjectTypeId)
        .is("deleted_at", null)
        .order("created_at", { ascending: true });

      if (error) throw new Error(error.message);
      return (data as Transform[]) ?? [];
    },
  });
  return { transforms: query.data ?? [], isLoading: query.isLoading, error: query.error };
}

/** Load a single transform by id. */
export function useTransform(id: string | null) {
  const query = useQuery({
    queryKey: [...TRANSFORMS_KEY, id],
    enabled: id !== null,
    queryFn: async () => {
      if (!id) return null;
      const { data, error } = await supabase.from("transforms").select("*").eq("id", id).single();

      if (error) throw new Error(error.message);
      return (data as Transform) ?? null;
    },
  });
  return { transform: query.data ?? null, isLoading: query.isLoading, error: query.error };
}

// ── Transform targets (multi-target) ─────────────────────────────────────────

/** Load the extra target Object Type ids for a transform (transform_targets join table). */
export function useTransformTargets(transformId: string | null) {
  const query = useQuery({
    queryKey: [...TRANSFORMS_KEY, "targets", transformId],
    enabled: transformId !== null,
    queryFn: async () => {
      if (!transformId) return [];
      const { data, error } = await supabase
        .from("transform_targets")
        .select("*")
        .eq("transform_id", transformId)
        .order("position", { ascending: true });

      if (error) throw new Error(error.message);
      return (data as TransformTarget[]) ?? [];
    },
  });
  return { targets: query.data ?? [], isLoading: query.isLoading, error: query.error };
}

// ── Mutations ─────────────────────────────────────────────────────────────────

interface SaveTransformArgs {
  id: string | null;
  expectedVersion: number | null; // null for new transforms
  input: SaveTransformInput;
  note?: string | null;
  /** Extra target Object Type ids (beyond the primary target_object_type_id). */
  extraTargetIds?: string[] | null;
}

export function useSaveTransform() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ id, expectedVersion, input, note, extraTargetIds }: SaveTransformArgs) => {
      const { data, error } = await supabase.rpc("save_transform", {
        p_id: id,
        p_expected_version: expectedVersion,
        p_input: input as unknown as Record<string, unknown>,
        p_note: note ?? null,
        // Pass null when not provided so the RPC treats it as "no-op for targets".
        // string[] is assignable to Json, so no cast is needed now that the
        // save_transform RPC overload is in the generated types.
        p_extra_target_ids: extraTargetIds ?? null,
      });

      if (error) throw mapRpcError(error);

      return data as Transform;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: TRANSFORMS_KEY });
    },
    onError: (err) => {
      console.error("hook:useSaveTransform", err);
      // StaleVersionError is handled by the caller (StaleVersionDialog); don't toast for it.
      if (!(err instanceof StaleVersionError)) {
        toast({
          title: "Failed to save transform",
          description: (err as Error).message,
          variant: "destructive",
        });
      }
    },
  });
}

export function useSoftDeleteTransform() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.rpc("soft_delete_transform", { p_id: id });
      if (error) throw mapRpcError(error);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: TRANSFORMS_KEY });
    },
    onError: (err) => {
      console.error("hook:useSoftDeleteTransform", err);
      toast({
        title: "Failed to delete transform",
        description: (err as Error).message,
        variant: "destructive",
      });
    },
  });
}

// ── Dry-run / run mutations ───────────────────────────────────────────────────

export function useRunTransform() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (args: RunTransformRequest) => {
      const response = await invokeEdgeFunction<RunTransformResponse>(
        "run-transform",
        args satisfies RunTransformRequest,
        { idempotent: false }
      );
      return response;
    },
    onSuccess: (_data, variables) => {
      if (!variables.dry_run) {
        // Invalidate transform runs so run history updates.
        queryClient.invalidateQueries({ queryKey: TRANSFORMS_KEY });
      }
    },
    onError: (err) => {
      console.error("hook:useRunTransform", err);
      toast({
        title: "Transform run failed",
        description: (err as Error).message,
        variant: "destructive",
      });
    },
  });
}
